import { Box, Button, Stack, Typography } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Fade, IconButton, InformationModal } from 'utoniq-ui'

import { GachaTicketSerialQuery, Maybe } from '@/clients/utoniq-core/schema'
import { BaseLayout, BottomSafeArea, Head } from '@/components/layouts'
import { FullscreenModal } from '@/components/organisms/functionals'
import { useMessage, User, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { setRedirectAfterLoginPath } from '@/core/native/storage'
import { CanvasConfetti, CanvasConfettiRef } from '@/lib/canvasConfetti'

export * from './fragments'

export type State = {
  loading: boolean
  submitting: boolean
  user: Maybe<User>
  gachaTicket: GachaTicketSerialQuery['gachaTicket']
}

export type Action = {
  onGetGachaTicket: () => Promise<void>
}

export const QRCodeGetGachaTicketTemplate: Template<State, Action> = ({
  state,
  onGetGachaTicket,
}) => {
  const { t } = useTranslation('qrCodeGetGachaTicket')
  const { router, routerNames, routerPages, generatePath } = useRouter()
  const { showError } = useMessage()

  const [open, setOpen] = useState<boolean>(false)
  const [completion, setCompletion] = useState<boolean>(false)

  return (
    <>
      <BaseLayout
        customNavigation={
          <FixedButtonCell
            onCheck={() => setOpen(true)}
            user={state.user}
            authenticatedAt={
              state.gachaTicket?.serial?.authentication?.authenticatedAt ?? ''
            }
          />
        }
        header={{
          title: completion ? t('title') : '',
          left: (
            <IconButton.Close
              edge="start"
              size="medium"
              onClick={() => router.push(routerPages.GlobalHome)}
            />
          ),
          type: 'modal',
        }}
        sx={{ height: '100dvh' }}
      >
        <Head title={routerNames.QRcodeGetGachaTicket} />
        {!state.loading && (
          <Fade style={{ height: '100%' }}>
            <Box sx={{ height: '100%' }}>
              <GachaTicketDescriptionCell gachaTicket={state.gachaTicket} />
            </Box>
          </Fade>
        )}
        <InformationModal
          open={open}
          onClose={() => setOpen(false)}
          title={t('titleConfirm')}
          content=""
          cancelText={t('cancelTextConfirm')}
          buttons={
            <Button
              size="large"
              color="primary"
              loading={state.submitting}
              onClick={async () => {
                await onGetGachaTicket()
                  .then(() => {
                    setCompletion(true)
                  })
                  .catch(e => showError(e))
                  .finally(() => setOpen(false))
              }}
              sx={{ minWidth: 212 }}
            >
              {t('okTextConfirm')}
            </Button>
          }
        />
      </BaseLayout>

      <CompletionModal open={completion} gachaTicket={state.gachaTicket} />
    </>
  )
}
type GachaTicketDescriptionCellProps = {
  gachaTicket: State['gachaTicket']
}
const GachaTicketDescriptionCell: Cell<GachaTicketDescriptionCellProps> = ({
  gachaTicket,
}) => {
  const { t } = useTranslation('qrCodeGetGachaTicket')

  return (
    <CellFrame
      sx={{
        display: 'grid',
        placeItems: 'center',
        height: '100%',
      }}
    >
      <Stack
        display="flex"
        justifyContent="center"
        alignItems="center"
        flex={1}
      >
        <GachaTicketCell gachaTicket={gachaTicket} />

        <Typography
          variant="subtitle1"
          align="center"
          color="text.secondary"
          sx={{ mt: 5 }}
        >
          {t('gachaTicketDescription')}
        </Typography>
      </Stack>
    </CellFrame>
  )
}

type FixedButtonCellProps = {
  user: State['user']
  authenticatedAt: string
  onCheck: () => void
}
const FixedButtonCell: Cell<FixedButtonCellProps> = ({
  user,
  authenticatedAt,
  onCheck,
}) => {
  const { routerPages, router } = useRouter()
  const { t } = useTranslation('qrCodeGetGachaTicket')
  const buttonText = useMemo(() => {
    if (!user) {
      return (
        <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>
          {t('onCheckTextNotLogin')}
        </Typography>
      )
    }
    if (authenticatedAt) return t('onCheckTextAcquired')

    return t('onCheckTextDefault')
  }, [authenticatedAt, t, user])

  return (
    <CellFrame>
      <BottomSafeArea sx={{ bgcolor: 'grey.50' }}>
        <Stack sx={{ py: 4, bgcolor: 'grey.50' }} alignItems="center">
          <Button
            size="large"
            color="primary"
            sx={{ width: 244 }}
            onClick={async () => {
              if (!user) {
                await setRedirectAfterLoginPath(router.pathname)
                return router.push(routerPages.Top)
              }
              if (!authenticatedAt) return onCheck()
            }}
            variant={!user ? 'text' : 'contained'}
            disabled={!!(user && authenticatedAt)}
          >
            {buttonText}
          </Button>
        </Stack>
      </BottomSafeArea>
    </CellFrame>
  )
}

type CompletionModalProps = {
  gachaTicket: State['gachaTicket']
  open: boolean
}
const CompletionModal: Cell<CompletionModalProps> = ({ gachaTicket, open }) => {
  const { t } = useTranslation('qrCodeGetGachaTicket')
  const { routerPages } = useRouter()
  const canvasConfettiRef = useRef<CanvasConfettiRef>(null)

  useEffect(() => {
    if (open && canvasConfettiRef.current) {
      canvasConfettiRef.current.start()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canvasConfettiRef.current])

  return (
    <FullscreenModal open={open}>
      <BaseLayout
        header={{ title: t('completionTitle') }}
        sx={{ height: '100dvh' }}
      >
        <Stack flex={1} sx={{ position: 'relative', height: '100%' }}>
          <Stack
            flex={1}
            sx={{ position: 'relative', zIndex: 1 }}
            alignItems="center"
            justifyContent="center"
          >
            <Fade>
              <GachaTicketCell gachaTicket={gachaTicket} />
            </Fade>
          </Stack>
          <Stack sx={{ pb: 5 }} spacing={5} alignItems="center">
            <Button
              variant="text"
              color="primary"
              href={routerPages.Top}
              sx={{ position: 'relative', zIndex: 1 }}
            >
              {t('goToTop')}
            </Button>
          </Stack>
          <CanvasConfetti ref={canvasConfettiRef} />
        </Stack>
      </BaseLayout>
    </FullscreenModal>
  )
}

type GachaTicketCellProps = {
  gachaTicket: State['gachaTicket']
}
const GachaTicketCell: Cell<GachaTicketCellProps> = ({ gachaTicket }) => {
  return (
    <CellFrame>
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        sx={{ pt: 4, pb: 6, px: 8, bgcolor: 'grey.50', borderRadius: 2 }}
      >
        <Box
          component="img"
          height={120}
          width={120}
          src={gachaTicket?.thumbnails?.reward?.uri || ''}
        />

        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{
            width: 104,
            height: 32,
            borderRadius: 8,
            bgcolor: 'grey.100',
          }}
        >
          <Typography variant="h5" align="center">
            x{gachaTicket?.serial?.issueCount}
          </Typography>
        </Stack>
      </Stack>
    </CellFrame>
  )
}
